/**
 * In-process stand-in for the agent WebSocket — development only.
 *
 * Implements the surface liveAgentSocket actually uses (send, close,
 * readyState, the four handlers, CONNECTING/OPEN), so the socket manager can
 * treat it exactly like a real WebSocket and no production code branches on
 * "are we mocking".
 *
 * Enabled by REACT_APP_LIVE_AGENT_MOCK=1 or ?liveMock=1.
 */

import { MOCK_SESSIONS, MOCK_TRANSCRIPTS, MOCK_INBOUND } from "./fixtures";

let turnCounter = 0;
const nextTurnId = () => `mock-turn-${Date.now()}-${turnCounter++}`;

export function isMockEnabled() {
  if (typeof window === "undefined") return false;
  if (process.env.REACT_APP_LIVE_AGENT_MOCK === "1") return true;
  try {
    return new URLSearchParams(window.location.search).get("liveMock") === "1";
  } catch {
    return false;
  }
}

export class MockLiveAgentSocket {
  static CONNECTING = 0;
  static OPEN = 1;
  static CLOSING = 2;
  static CLOSED = 3;

  constructor(url) {
    this.url = url;
    this.readyState = MockLiveAgentSocket.CONNECTING;
    this.onopen = null;
    this.onmessage = null;
    this.onerror = null;
    this.onclose = null;

    // Mutable copies so pause/resume and message counts actually move.
    this._sessions = MOCK_SESSIONS.map((s) => ({ ...s }));
    this._timers = [];
    this._inboundIdx = 0;

    this._timers.push(setTimeout(() => this._open(), 250));

    // Expose the chaos hooks so a developer can exercise reconnect paths from
    // the console without a backend.
    if (typeof window !== "undefined") {
      window.__LIVE_MOCK__ = {
        forceClose: (code = 1006) => this._close(code),
        expire: () => this._close(4401),
        forbid: () => this._close(4403),
        message: (sessionId, text) => this._inbound(sessionId, text),
        takeover: (sessionId, name = "Another Agent") =>
          this._remoteTakeover(sessionId, name),
        // Simulate another agent asking YOU (the handler) for control, so you can
        // exercise the Accept/Reject prompt: __LIVE_MOCK__.request('sess-aurora-1').
        request: (sessionId, name = "Sana") => {
          const s = this._sessions.find((x) => x.session_id === sessionId);
          if (!s) return;
          this._emit({
            type: "takeover_request",
            session_id: sessionId,
            conversation_id: s.conversation_id,
            handler_id: 1,
            handler_name: "You",
            requester_id: 888,
            requester_name: name,
          });
        },
        sessions: () => this._sessions,
      };
    }
  }

  // ── inbound plumbing ──────────────────────────────────────────────────────

  _emit(payload) {
    if (this.readyState !== MockLiveAgentSocket.OPEN) return;
    this.onmessage?.({ data: JSON.stringify(payload) });
  }

  _open() {
    this.readyState = MockLiveAgentSocket.OPEN;
    this.onopen?.();
    this._emit({ type: "live_list", sessions: this._sessions });
    this._scheduleInbound();
  }

  /** Periodic customer traffic, so the panel visibly does something. */
  _scheduleInbound() {
    const delay = 7000 + Math.random() * 5000;
    this._timers.push(
      setTimeout(() => {
        if (this.readyState !== MockLiveAgentSocket.OPEN) return;
        const open = this._sessions.filter((s) => !s.closedAt);
        if (open.length) {
          const target = open[Math.floor(Math.random() * open.length)];
          this._inbound(
            target.session_id,
            MOCK_INBOUND[this._inboundIdx++ % MOCK_INBOUND.length]
          );
        }
        this._scheduleInbound();
      }, delay)
    );
  }

  _inbound(sessionId, text) {
    const session = this._sessions.find((s) => s.session_id === sessionId);
    if (!session) return;
    session.message_count += 1;
    session.last_activity_at = new Date().toISOString();

    const base = {
      session_id: sessionId,
      conversation_id: session.conversation_id,
      text,
      turn_id: nextTurnId(),
      created_at: new Date().toISOString(),
    };
    // Both frames carry the same turn_id — this is exactly the overlap the
    // store's dedupe has to survive.
    this._emit({ type: "notify", ...base });
    this._emit({ type: "customer_message", ...base, log_kind: "cust" });
  }

  _remoteTakeover(sessionId, name) {
    const session = this._sessions.find((s) => s.session_id === sessionId);
    if (!session) return;
    session.is_ai_paused = true;
    session.handling_agent_id = 999;
    session.handling_agent_name = name;
    this._emit({
      type: "mode_change",
      session_id: sessionId,
      conversation_id: session.conversation_id,
      mode: "human",
      handling_agent_name: name,
      handling_agent_id: 999,
      reason: "agent_takeover",
    });
  }

  // ── outbound (what the console sends us) ──────────────────────────────────

  send(raw) {
    let frame;
    try {
      frame = JSON.parse(raw);
    } catch {
      return;
    }

    const session = this._sessions.find(
      (s) => s.session_id === frame.session_id
    );

    switch (frame.type) {
      case "ping":
        return this._emit({ type: "pong" });

      case "list":
        // Authoritative re-sync (self-heal), mirroring the real backend.
        return this._emit({
          type: "live_list",
          sessions: this._sessions,
          reason: "refresh",
        });

      case "subscribe":
        return this._timers.push(
          setTimeout(() => {
            this._emit({
              type: "transcript",
              session_id: frame.session_id,
              turns: MOCK_TRANSCRIPTS[frame.session_id] || [],
            });
          }, 220)
        );

      case "pause":
      case "resume": {
        if (!session) return;
        const mode = frame.type === "pause" ? "human" : "ai";
        session.is_ai_paused = mode === "human";
        session.handling_agent_id = mode === "human" ? 1 : null;
        session.handling_agent_name = mode === "human" ? "You" : null;
        return this._timers.push(
          setTimeout(() => {
            this._emit({
              type: "mode",
              session_id: frame.session_id,
              conversation_id: session.conversation_id,
              mode,
              handling_agent_id: session.handling_agent_id,
              handling_agent_name: session.handling_agent_name,
            });
            if (mode === "human") {
              this._emit({
                type: "system",
                session_id: frame.session_id,
                text: "You joined the conversation.",
                turn_id: nextTurnId(),
                created_at: new Date().toISOString(),
                log_kind: "system",
              });
            }
          }, 300)
        );
      }

      case "agent_message": {
        if (!session) return;
        // Echo back as the server would, including the client_msg_id so
        // optimistic reconciliation takes its exact-match path.
        return this._timers.push(
          setTimeout(() => {
            this._emit({
              type: "agent_message",
              session_id: frame.session_id,
              conversation_id: session.conversation_id,
              text: frame.text,
              client_msg_id: frame.client_msg_id,
              agent_name: "You",
              is_bot: false,
              turn_id: nextTurnId(),
              created_at: new Date().toISOString(),
              log_kind: "agent",
            });
            // A plausible visitor reply, so the thread feels alive.
            this._timers.push(
              setTimeout(
                () => this._inbound(frame.session_id, "Got it, thank you!"),
                2600
              )
            );
          }, 420)
        );
      }

      // WS2 handoff. I ("You", id 1) request control of a chat another agent holds;
      // the mock echoes the pending request then auto-accepts after a beat.
      case "takeover_request": {
        if (
          !session ||
          !session.is_ai_paused ||
          session.handling_agent_id === 1
        )
          return;
        this._emit({
          type: "takeover_request",
          session_id: frame.session_id,
          conversation_id: session.conversation_id,
          handler_id: session.handling_agent_id,
          handler_name: session.handling_agent_name || "Agent",
          requester_id: 1,
          requester_name: "You",
        });
        return this._timers.push(
          setTimeout(() => {
            session.is_ai_paused = true;
            session.handling_agent_id = 1;
            session.handling_agent_name = "You";
            this._emit({
              type: "takeover_close",
              session_id: frame.session_id,
              requester_id: 1,
              status: "accepted",
            });
            this._emit({
              type: "mode",
              session_id: frame.session_id,
              conversation_id: session.conversation_id,
              mode: "human",
              handling_agent_id: 1,
              handling_agent_name: "You",
            });
          }, 1800)
        );
      }

      case "takeover_cancel":
        return this._emit({
          type: "takeover_close",
          session_id: frame.session_id,
          requester_id: 1,
          status: "cancelled",
        });

      // I'm the handler answering an incoming request (see __LIVE_MOCK__.request()).
      case "takeover_response": {
        if (!session) return;
        const status = frame.accept ? "accepted" : "rejected";
        if (frame.accept) {
          session.handling_agent_id = 888;
          session.handling_agent_name = "Sana";
          this._emit({
            type: "mode",
            session_id: frame.session_id,
            conversation_id: session.conversation_id,
            mode: "human",
            handling_agent_id: 888,
            handling_agent_name: "Sana",
          });
        }
        return this._emit({
          type: "takeover_close",
          session_id: frame.session_id,
          requester_id: 888,
          status,
        });
      }

      default:
        break;
    }
  }

  close(code = 1000) {
    this._close(code);
  }

  _close(code) {
    if (this.readyState === MockLiveAgentSocket.CLOSED) return;
    this.readyState = MockLiveAgentSocket.CLOSED;
    this._timers.forEach(clearTimeout);
    this._timers = [];
    this.onclose?.({ code });
  }
}
